import { createContext, ReactNode, useContext } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { InsertUser, LoginUser, User } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginUser>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, InsertUser>;
  isAuthenticated: boolean;
  isTrialExpired: boolean;
  hasActiveSubscription: boolean;
  isFounder: boolean;
  isOfficialBearInsider: boolean;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  
  const {
    data: userResponse,
    error,
    isLoading,
  } = useQuery<{ user: User } | undefined, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
  });

  const user = userResponse?.user || null;

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginUser) => {
      const res = await apiRequest("POST", "/api/login", credentials);
      const data = await res.json();
      // Store JWT token for stateless authentication (fallback for serverless environments)
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      return data.user;
    },
    onSuccess: async (user: User) => {
      // Set query data and invalidate to ensure React Query updates
      queryClient.setQueryData(["/api/user"], { user });
      // Invalidate the user query to trigger a refetch and ensure state updates
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      // Force refetch all authenticated queries after login
      await queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
      
      // Check if cookies are being blocked (common in iframe environments)
      const checkAuthRes = await fetch("/api/user", { credentials: "include" });
      if (!checkAuthRes.ok) {
        toast({
          title: "Login successful but session not persisting",
          description: "Your browser may be blocking cookies. Try opening the app in a new tab or allowing third-party cookies.",
          variant: "destructive",
          duration: 10000,
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "You've successfully logged in.",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: "Please check your email and password.",
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: InsertUser) => {
      const res = await apiRequest("POST", "/api/register", credentials);
      const data = await res.json();
      // Store JWT token for stateless authentication (fallback for serverless environments)
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      return data.user;
    },
    onSuccess: async (user: User) => {
      // Set query data and invalidate to ensure React Query updates
      queryClient.setQueryData(["/api/user"], { user });
      // Invalidate the user query to trigger a refetch and ensure state updates
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      // Force refetch all authenticated queries after registration
      await queryClient.invalidateQueries({ queryKey: ["/api/stories"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
      toast({
        title: "Welcome!",
        description: "Your account has been created. Enjoy your 7-day free trial!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/logout");
    },
    onSuccess: () => {
      // Clear JWT token on logout
      localStorage.removeItem('auth_token');
      queryClient.setQueryData(["/api/user"], null);
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Helper computed values
  const isAuthenticated = !!user;
  
  const isTrialExpired = user ? 
    (user.trialEndsAt ? new Date(user.trialEndsAt) < new Date() : false) : false;
  
  // Match backend isSubscribed() logic exactly (server/services/subscriptionService.ts)
  const hasActiveSubscription = !!user && (
    user.subscriptionStatus === 'lifetime' || 
    (user.subscriptionStatus === 'active' && 
      !!user.subscriptionEndsAt && 
      new Date(user.subscriptionEndsAt) > new Date()) || 
    (user.subscriptionStatus === 'trial' && 
      !!user.trialEndsAt && 
      new Date(user.trialEndsAt) > new Date())
  );
  

  // Special founder detection for Penelope (Craig.Ross@neuralquantum.ai)
  const isFounder = user?.email?.toLowerCase() === 'craig.ross@neuralquantum.ai';
  
  // Official Bear Insider verification status
  const isOfficialBearInsider = user?.isOfficialBearInsider || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        isAuthenticated,
        isTrialExpired,
        hasActiveSubscription,
        isFounder,
        isOfficialBearInsider,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}